import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Users, Lock, Unlock, Search, PenLine } from 'lucide-react';

// Supplier categories eligible to own a vehicle externally.
// NB : `suppliers.category` est un `text` libre dans le schéma (yaml_cli show supplier),
// PAS un enum — ce commentaire renvoyait à une ligne du YAML sans rapport (audit 07/09,
// lot 1.13). Aucune contrainte serveur ne valide ces valeurs : la liste ci-dessous est
// une convention du wizard, à garder alignée avec ce que le formulaire fournisseur écrit.
// Covers the 95% case: leasing companies, LLD/LOA renters, banks filed under 'other'.
const OWNER_SUPPLIER_CATEGORIES = new Set(['leasing', 'rental', 'other']);

// Libellés : vehicles.wizard.owner.categories.<code>
const SUPPLIER_CATEGORIES = ['leasing', 'rental', 'other'];

/**
 * StepOwner - Step 2 of VehicleLifecycleWorkflow
 *
 * Captures vehicle owner information:
 * - Owner type: Tenant (default) or External (leasing company, etc.)
 * - If Tenant: displays tenant details (read-only)
 * - If External: editable fields for owner identification
 * - Legal status: Pledged (gagé) or Not Pledged (non gagé)
 *
 * @param {Object} props
 * @param {import('../VehicleLifecycleWorkflow').VehicleFormData} props.formData - Current form state
 * @param {(field: string, value: any) => void} props.updateFormData - Update a form field
 * @param {Object} props.tenant - Tenant data from AuthContext (includes billing fields)
 * @returns {JSX.Element}
 */
function StepOwner({ formData, updateFormData, tenant, suppliers = [] }) {
  const { t } = useTranslation();
  const categoryLabel = (cat) => t(`vehicles.wizard.owner.categories.${cat}`, { defaultValue: cat });
  // 'select' uses the supplier referential, 'manual' captures a one-off owner
  // and auto-creates a supplier on submit (mirrors the claims garage flow).
  const [ownerMode, setOwnerMode] = useState(() =>
    formData.owner_supplier_id ? 'select' : 'manual'
  );
  const [ownerCategoryFilter, setOwnerCategoryFilter] = useState(
    formData.owner_supplier_category || 'leasing'
  );

  const eligibleSuppliers = useMemo(() => {
    return (suppliers || [])
      .filter(s => OWNER_SUPPLIER_CATEGORIES.has(s.category))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [suppliers]);

  const filteredSuppliers = useMemo(() => {
    return eligibleSuppliers.filter(s => s.category === ownerCategoryFilter);
  }, [eligibleSuppliers, ownerCategoryFilter]);

  const applySupplier = (supplier) => {
    if (!supplier) return;
    updateFormData('owner_supplier_id', supplier.id);
    updateFormData('owner_supplier_category', supplier.category);
    updateFormData('owner_name', supplier.name || '');
    updateFormData('owner_address', supplier.address || '');
    updateFormData('owner_postal_code', supplier.postal_code || '');
    updateFormData('owner_city', supplier.city || '');
    updateFormData('owner_email', supplier.email || '');
    updateFormData('owner_phone', supplier.phone || '');
  };

  const clearSupplierLink = () => {
    updateFormData('owner_supplier_id', null);
    updateFormData('owner_supplier_category', null);
  };

  // Format tenant address for display
  const formatTenantAddress = () => {
    const parts = [];
    if (tenant?.billing_address_line1) parts.push(tenant.billing_address_line1);
    if (tenant?.billing_address_line2) parts.push(tenant.billing_address_line2);
    if (tenant?.billing_postal_code || tenant?.billing_city) {
      parts.push(`${tenant.billing_postal_code || ''} ${tenant.billing_city || ''}`.trim());
    }
    if (tenant?.billing_country && tenant.billing_country !== 'FR') {
      parts.push(tenant.billing_country);
    }
    return parts.join(', ') || t('vehicles.wizard.owner.notProvidedF');
  };

  // Check if tenant billing info is complete
  const isTenantInfoComplete = tenant?.billing_name || tenant?.name;

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('vehicles.wizard.owner.title')}</h3>

      {/* Owner Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">{t('vehicles.wizard.owner.ownerType')}</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Tenant Option */}
          <button
            type="button"
            onClick={() => {
              updateFormData('owner_type', 'tenant');
              // Clear external owner fields when switching to tenant
              updateFormData('owner_name', '');
              updateFormData('owner_address', '');
              updateFormData('owner_postal_code', '');
              updateFormData('owner_city', '');
              updateFormData('owner_email', '');
              updateFormData('owner_phone', '');
              updateFormData('owner_supplier_id', null);
              updateFormData('owner_supplier_category', null);
            }}
            className={`p-4 border-2 rounded-lg flex items-start gap-3 transition-all text-left ${
              formData.owner_type === 'tenant'
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-300 hover:border-amber-300'
            }`}
          >
            <Building2 className={`w-6 h-6 mt-0.5 ${formData.owner_type === 'tenant' ? 'text-amber-600' : 'text-gray-400'}`} />
            <div>
              <span className={`font-medium block ${formData.owner_type === 'tenant' ? 'text-amber-700' : 'text-gray-900'}`}>
                {tenant?.billing_name || tenant?.name || t('vehicles.wizard.owner.company')}
              </span>
              <span className="text-sm text-gray-500">{t('vehicles.wizard.owner.tenantHint')}</span>
            </div>
          </button>

          {/* External Owner Option */}
          <button
            type="button"
            onClick={() => updateFormData('owner_type', 'external')}
            className={`p-4 border-2 rounded-lg flex items-start gap-3 transition-all text-left ${
              formData.owner_type === 'external'
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-300 hover:border-amber-300'
            }`}
          >
            <Users className={`w-6 h-6 mt-0.5 ${formData.owner_type === 'external' ? 'text-amber-600' : 'text-gray-400'}`} />
            <div>
              <span className={`font-medium block ${formData.owner_type === 'external' ? 'text-amber-700' : 'text-gray-900'}`}>
                {t('vehicles.wizard.owner.external')}
              </span>
              <span className="text-sm text-gray-500">{t('vehicles.wizard.owner.externalHint')}</span>
            </div>
          </button>
        </div>
      </div>

      {/* Tenant Details (Read-only) */}
      {formData.owner_type === 'tenant' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t('vehicles.wizard.owner.contactTitle')}
          </h4>

          {!isTenantInfoComplete && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
              {t('vehicles.wizard.owner.incompleteWarning')}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">{t('vehicles.wizard.owner.companyName')}</span>
              <p className="font-medium text-gray-900">{tenant?.billing_name || tenant?.name || t('vehicles.wizard.owner.notProvidedF')}</p>
            </div>
            <div>
              <span className="text-gray-500">{t('vehicles.wizard.owner.address')}</span>
              <p className="font-medium text-gray-900">{formatTenantAddress()}</p>
            </div>
            <div>
              <span className="text-gray-500">{t('vehicles.wizard.owner.email')}</span>
              <p className="font-medium text-gray-900">{tenant?.billing_email || t('vehicles.wizard.owner.notProvided')}</p>
            </div>
            <div>
              <span className="text-gray-500">{t('vehicles.wizard.owner.phone')}</span>
              <p className="font-medium text-gray-900">{tenant?.billing_phone || t('vehicles.wizard.owner.notProvided')}</p>
            </div>
          </div>
        </div>
      )}

      {/* External Owner — select from supplier referential or enter manually */}
      {formData.owner_type === 'external' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h4 className="font-semibold text-gray-900">{t('vehicles.wizard.owner.externalContactTitle')}</h4>
            <div className="flex bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setOwnerMode('select')}
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                  ownerMode === 'select' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Search className="w-3 h-3" /> {t('vehicles.wizard.owner.choose')}
              </button>
              <button
                type="button"
                onClick={() => { setOwnerMode('manual'); clearSupplierLink(); }}
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                  ownerMode === 'manual' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <PenLine className="w-3 h-3" /> {t('vehicles.wizard.owner.enter')}
              </button>
            </div>
          </div>

          {ownerMode === 'select' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vehicles.wizard.owner.ownerCategory')}</label>
                <div className="flex gap-2 flex-wrap">
                  {SUPPLIER_CATEGORIES.map((cat) => (
                    <button key={cat} type="button"
                      onClick={() => setOwnerCategoryFilter(cat)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-colors ${
                        ownerCategoryFilter === cat
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-200 text-gray-600 hover:border-amber-300'
                      }`}>
                      {categoryLabel(cat)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vehicles.wizard.owner.selectSupplier')}</label>
                <select
                  value={formData.owner_supplier_id || ''}
                  onChange={(e) => {
                    const id = parseInt(e.target.value);
                    const supplier = filteredSuppliers.find(s => s.id === id);
                    if (supplier) applySupplier(supplier);
                    else clearSupplierLink();
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="">{t('common.actions.selectPlaceholder')}</option>
                  {filteredSuppliers.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.city ? ` — ${s.city}` : ''}
                    </option>
                  ))}
                </select>
                {filteredSuppliers.length === 0 && (
                  <p className="text-xs text-amber-600 mt-2">
                    {t('vehicles.wizard.owner.noSupplier', { category: categoryLabel(ownerCategoryFilter) })}
                  </p>
                )}
                {formData.owner_supplier_id && (
                  <div className="mt-3 text-sm text-gray-700 bg-white rounded-lg p-3 space-y-1 border border-gray-200">
                    <div><span className="font-medium">{t('vehicles.wizard.owner.summaryCompany')}</span> {formData.owner_name}</div>
                    {formData.owner_address && <div><span className="font-medium">{t('vehicles.wizard.owner.summaryAddress')}</span> {[formData.owner_address, formData.owner_postal_code, formData.owner_city].filter(Boolean).join(', ')}</div>}
                    {formData.owner_email && <div><span className="font-medium">{t('vehicles.wizard.owner.summaryEmail')}</span> {formData.owner_email}</div>}
                    {formData.owner_phone && <div><span className="font-medium">{t('vehicles.wizard.owner.summaryPhone')}</span> {formData.owner_phone}</div>}
                  </div>
                )}
              </div>
            </div>
          )}

          {ownerMode === 'manual' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vehicles.wizard.owner.category')}</label>
                <select
                  value={formData.owner_supplier_category || 'leasing'}
                  onChange={(e) => updateFormData('owner_supplier_category', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  {SUPPLIER_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{categoryLabel(cat)}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  {t('vehicles.wizard.owner.autoCreateHint')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.owner.companyRequired')}</label>
                  <input
                    type="text"
                    value={formData.owner_name || ''}
                    onChange={(e) => updateFormData('owner_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder={t('vehicles.wizard.owner.companyPlaceholder')}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.owner.address')}</label>
                  <input
                    type="text"
                    value={formData.owner_address || ''}
                    onChange={(e) => updateFormData('owner_address', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder={t('vehicles.wizard.owner.addressPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.owner.postalCode')}</label>
                  <input
                    type="text"
                    value={formData.owner_postal_code || ''}
                    onChange={(e) => updateFormData('owner_postal_code', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder={t('vehicles.wizard.owner.postalCodePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.owner.city')}</label>
                  <input
                    type="text"
                    value={formData.owner_city || ''}
                    onChange={(e) => updateFormData('owner_city', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder={t('vehicles.wizard.owner.cityPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.owner.contactEmail')}</label>
                  <input
                    type="email"
                    value={formData.owner_email || ''}
                    onChange={(e) => updateFormData('owner_email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="contact@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.owner.contactPhone')}</label>
                  <input
                    type="tel"
                    value={formData.owner_phone || ''}
                    onChange={(e) => updateFormData('owner_phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder={t('vehicles.wizard.owner.phonePlaceholder')}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legal Status Section */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="font-semibold text-gray-900 mb-3">{t('vehicles.wizard.owner.legalTitle')}</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => updateFormData('is_mortgaged', false)}
            className={`p-4 border-2 rounded-lg flex items-center gap-3 transition-all ${
              !formData.is_mortgaged
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 hover:border-green-300'
            }`}
          >
            <Unlock className={`w-6 h-6 ${!formData.is_mortgaged ? 'text-green-600' : 'text-gray-400'}`} />
            <div className="text-left">
              <span className={`font-medium block ${!formData.is_mortgaged ? 'text-green-700' : 'text-gray-900'}`}>
                {t('vehicles.wizard.owner.notPledged')}
              </span>
              <span className="text-sm text-gray-500">{t('vehicles.wizard.owner.notPledgedHint')}</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => updateFormData('is_mortgaged', true)}
            className={`p-4 border-2 rounded-lg flex items-center gap-3 transition-all ${
              formData.is_mortgaged
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-300 hover:border-amber-300'
            }`}
          >
            <Lock className={`w-6 h-6 ${formData.is_mortgaged ? 'text-amber-600' : 'text-gray-400'}`} />
            <div className="text-left">
              <span className={`font-medium block ${formData.is_mortgaged ? 'text-amber-700' : 'text-gray-900'}`}>
                {t('vehicles.wizard.owner.pledged')}
              </span>
              <span className="text-sm text-gray-500">{t('vehicles.wizard.owner.pledgedHint')}</span>
            </div>
          </button>
        </div>

        {formData.is_mortgaged && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            {t('vehicles.wizard.owner.pledgedWarning')}
          </div>
        )}
      </div>
    </div>
  );
}

export default StepOwner;
